// src/utils/guestRoomAnalytics.js
// Pure helpers for Guest Room Analytics. Every card value is derived from the
// SAME rows shown in its drill-down popup, so cards, popups and Excel exports
// always reconcile. Everything is derived from the CURRENT booking state.

import {
  parseISO, format, getYear,
  startOfMonth, endOfMonth,
  startOfDay, endOfDay, subMonths, differenceInCalendarDays, isWithinInterval,
} from "date-fns";

export const PENDING_STATUSES = ["booked", "checked_in", "checked_out"];
export const CANCELLED_STATUSES = ["cancelled", "no_show"];

const STATUS_LABELS = {
  booked: "Booked",
  checked_in: "Checked In",
  checked_out: "Checked Out",
  cancelled: "Cancelled",
  no_show: "No Show",
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const hasValue = (value) =>
  value !== undefined && value !== null && value !== "" && Number.isFinite(Number(value));

const toDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value === "string" && value) return parseISO(value);
  return null;
};

export const statusLabel = (status) => STATUS_LABELS[status] || status || "—";

export const sumBy = (rows, key) => rows.reduce((sum, row) => sum + num(row[key]), 0);

/* ------------------------------------------------------------------ */
/* Per-booking money rules                                             */
/* ------------------------------------------------------------------ */

/** max(0, total - paid - discount) */
export const getDerivedBalance = (booking) =>
  Math.max(0, num(booking.totalAmount) - num(booking.paidAmount) - num(booking.discount));

/** Stored balanceAmount when present, otherwise the derived balance. Never negative. */
export const getPendingBalance = (booking) =>
  hasValue(booking.balanceAmount)
    ? Math.max(0, num(booking.balanceAmount))
    : getDerivedBalance(booking);

/**
 * Money still receivable. Only Paid bookings that are booked / checked in /
 * checked out can be receivable; cancelled, no-show and Free are always 0.
 */
export const getReceivableBalance = (booking) => {
  if (booking.paymentType !== "Paid") return 0;
  if (!PENDING_STATUSES.includes(booking.status)) return 0;
  return getPendingBalance(booking);
};

/**
 * Amount expected but made non-receivable by cancellation / no-show.
 * Uses the derived formula because auto-cancel leaves a stale balanceAmount.
 */
export const getCancelledContribution = (booking) => {
  if (booking.paymentType !== "Paid") return 0;
  if (!CANCELLED_STATUSES.includes(booking.status)) return 0;
  return getDerivedBalance(booking);
};

/** Money actually received: paidAmount on Paid bookings. */
export const getReceivedAmount = (booking) =>
  booking.paymentType === "Paid" ? Math.max(0, num(booking.paidAmount)) : 0;

/**
 * Effective checkout date/time. Mirrors backend autoCheckoutOverdueGuests
 * (bookingController): (extensionDate || to) + checkOutTime, default "12:00",
 * applied in local time. Returns null when it cannot be computed.
 */
export const getEffectiveCheckoutDateTime = (booking) => {
  const base = toDate(booking.extensionDate) || toDate(booking.to);
  if (!base) return null;
  const checkout = new Date(base);
  const [hours, minutes] = (booking.checkOutTime || "12:00").split(":");
  checkout.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
  return Number.isNaN(checkout.getTime()) ? null : checkout;
};

/**
 * Active only when status is checked_in AND now is before the effective
 * checkout (now >= checkout is overdue, exactly like the auto-checkout cron).
 */
export const isActiveGuest = (booking, now = new Date()) => {
  if (booking.status !== "checked_in") return false;
  const checkout = getEffectiveCheckoutDateTime(booking);
  return !checkout || now < checkout;
};

/* ------------------------------------------------------------------ */
/* Range                                                               */
/* ------------------------------------------------------------------ */

const ACTIVE_BASE = "Currently checked in";

const fmtDay = (date) => format(date, "d MMM yyyy");

/** Short "15 Aug – 20 Sep 2026" (year shown once when both dates share it). */
const fmtSpan = (start, end) =>
  start.getFullYear() === end.getFullYear()
    ? `${format(start, "d MMM")} – ${fmtDay(end)}`
    : `${fmtDay(start)} – ${fmtDay(end)}`;

/** Accepts a Date or a yyyy-MM-dd string; returns a valid start-of-day Date or null. */
const toDay = (value) => {
  const date = toDate(value);
  return date && !Number.isNaN(date.getTime()) ? startOfDay(date) : null;
};

/**
 * Validates a custom range ({ from, to } as Dates or yyyy-MM-dd strings).
 * Returns { valid, message, start, end }.
 */
export const validateCustomRange = (customRange) => {
  const start = toDay(customRange?.from);
  const end = toDay(customRange?.to);
  if (!start) return { valid: false, message: "Please select a From date.", start, end };
  if (!end) return { valid: false, message: "Please select a To date.", start, end };
  if (start > end) return { valid: false, message: "From date cannot be after To date.", start, end };
  return { valid: true, message: "", start, end };
};

/**
 * Window for the selected range (all based on the booking check-in `from` date):
 * Monthly   = current calendar month (whole month, future days included)
 * Quarterly = last 3 calendar months incl. the current one
 * Annual    = last 12 calendar months incl. the current one
 * Custom    = { from, to } picked by the user, inclusive
 * Overall   = no boundary
 * `activeSubtitle` explains the Active Guests check-in cut-off.
 */
export const getAnalyticsRangeWindow = (range, now = new Date(), customRange = null) => {
  let window;
  if (range === "Custom") {
    const custom = validateCustomRange(customRange);
    if (custom.valid) {
      return {
        start: custom.start,
        end: endOfDay(custom.end),
        subtitle: fmtSpan(custom.start, custom.end),
        activeSubtitle: `${ACTIVE_BASE} • Check-in ${fmtSpan(custom.start, custom.end)}`,
      };
    }
    range = "Monthly";
  }
  if (range === "Monthly") {
    window = { start: startOfMonth(now), end: endOfMonth(now), subtitle: `Current month (${format(now, "MMMM yyyy")})` };
  } else if (range === "Quarterly") {
    const start = startOfMonth(subMonths(now, 2));
    window = { start, end: endOfMonth(now), subtitle: `Last 3 months (${format(start, "MMM yyyy")} – ${format(now, "MMM yyyy")})` };
  } else if (range === "Annual") {
    const start = startOfMonth(subMonths(now, 11));
    window = { start, end: endOfMonth(now), subtitle: `Last 12 months (${format(start, "MMM yyyy")} – ${format(now, "MMM yyyy")})` };
  } else {
    return { start: null, end: null, subtitle: "All time", activeSubtitle: ACTIVE_BASE };
  }
  return {
    ...window,
    activeSubtitle: `${ACTIVE_BASE} • Check-in from ${fmtDay(window.start)} onwards`,
  };
};

/** Period is based on the booking check-in (`from`) date, inclusive of both ends. */
export const getAnalyticsBookingsForRange = (bookings, range, now = new Date(), customRange = null) => {
  const window = getAnalyticsRangeWindow(range, now, customRange);
  if (!window.start || !window.end) return bookings;

  return bookings.filter((booking) => {
    const from = toDate(booking.from);
    if (!from || Number.isNaN(from.getTime())) return false;
    return isWithinInterval(startOfDay(from), {
      start: startOfDay(window.start),
      end: startOfDay(window.end),
    });
  });
};

/**
 * Active Guests: CURRENT status is checked_in AND the actual check-in date
 * (fallback: `from`) is on/after `periodStart` and (custom range only) on/before
 * `periodEnd`. No cut-off for Overall.
 */
export const getActiveGuestBookings = (bookings, periodStart = null, periodEnd = null, now = new Date()) =>
  bookings.filter((booking) => {
    if (!isActiveGuest(booking, now)) return false;
    if (!periodStart && !periodEnd) return true;
    const checkIn = toDate(booking.actualCheckInDate) || toDate(booking.from);
    if (!checkIn || Number.isNaN(checkIn.getTime())) return false;
    const day = startOfDay(checkIn);
    if (periodStart && day < startOfDay(periodStart)) return false;
    if (periodEnd && day > startOfDay(periodEnd)) return false;
    return true;
  });

/* ------------------------------------------------------------------ */
/* Rows                                                                */
/* ------------------------------------------------------------------ */

const paymentResponsibilityLabel = (value) => (value === "DEPARTMENT" ? "Department" : "Guest");

const baseRow = (booking) => ({
  id: booking.id || booking._id || booking.bookingId,
  bookingId: booking.bookingId || "Pending ID assignment",
  guest: booking.guest || "—",
  contact: booking.contact || "—",
  hostel: booking.hostel || "—",
  roomNo: booking.roomNo || "—",
  from: toDate(booking.from),
  to: toDate(booking.to),
  expectedCheckout: toDate(booking.extensionDate) || toDate(booking.to),
  status: booking.status,
  statusLabel: statusLabel(booking.status),
  paymentType: booking.paymentType || "—",
  paymentResponsibility: paymentResponsibilityLabel(booking.paymentResponsibility),
  isDepartment: booking.paymentResponsibility === "DEPARTMENT",
  paymentStatus: booking.paymentStatus || "—",
  paymentMode: booking.paymentMode || "—",
  transactionId: booking.transactionId || "—",
  totalAmount: num(booking.totalAmount),
  paidAmount: num(booking.paidAmount),
  discount: num(booking.discount),
  balance: getReceivableBalance(booking),
});

export const getTotalBookingRows = (bookings) => bookings.map(baseRow);

export const getActiveGuestRows = (bookings, periodStart = null, periodEnd = null, now = new Date()) =>
  getActiveGuestBookings(bookings, periodStart, periodEnd, now).map((booking) => ({
    ...baseRow(booking),
    pendingBalance: getReceivableBalance(booking),
  }));

export const getPendingRows = (bookings) =>
  bookings
    .map((booking) => ({ booking, amount: getReceivableBalance(booking) }))
    .filter(({ amount }) => amount > 0)
    .map(({ booking, amount }) => {
      const row = baseRow(booking);
      return {
        ...row,
        amount,
        category: row.isDepartment ? "Department Pay" : row.statusLabel,
      };
    });

export const getCancelledRows = (bookings) =>
  bookings
    .map((booking) => ({ booking, amount: getCancelledContribution(booking) }))
    .filter(({ amount }) => amount > 0)
    .map(({ booking, amount }) => ({ ...baseRow(booking), amount }));

export const getReceivedRows = (bookings) =>
  bookings
    .map((booking) => ({ booking, amount: getReceivedAmount(booking) }))
    .filter(({ amount }) => amount > 0)
    .map(({ booking, amount }) => ({ ...baseRow(booking), amount }));

/**
 * One call for the page: rows per metric + the card values derived from them.
 * `bookings` is the period population (by `from`). Active Guests is current-state,
 * so it is drawn from `allBookings` and cut off by `activeFrom` (the period start) and, for Custom, `activeTo`.
 */
export const buildAnalyticsMetrics = (bookings, { allBookings = bookings, activeFrom = null, activeTo = null, now = new Date() } = {}) => {
  const total = getTotalBookingRows(bookings);
  const received = getReceivedRows(bookings);
  const active = getActiveGuestRows(allBookings, activeFrom, activeTo, now);
  const pending = getPendingRows(bookings);
  const cancelled = getCancelledRows(bookings);

  return {
    rows: { total, received, active, pending, cancelled },
    cards: {
      totalBookings: total.length,
      amountReceived: sumBy(received, "amount"),
      activeGuests: active.length,
      pendingAmount: sumBy(pending, "amount"),
      cancelledAmount: sumBy(cancelled, "amount"),
    },
  };
};

/* ------------------------------------------------------------------ */
/* Popup column config (shared by the modal and the Excel export)      */
/* ------------------------------------------------------------------ */

const fmtDate = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime()) ? format(date, "dd-MMM-yyyy") : "—";

const COL = {
  bookingId: { key: "bookingId", label: "Booking ID" },
  guest: { key: "guest", label: "Guest" },
  contact: { key: "contact", label: "Contact" },
  hostel: { key: "hostel", label: "Hostel" },
  roomNo: { key: "roomNo", label: "Room" },
  from: { key: "from", label: "Check-in", type: "date" },
  to: { key: "to", label: "Check-out", type: "date" },
  expectedCheckout: { key: "expectedCheckout", label: "Expected Check-out", type: "date" },
  statusLabel: { key: "statusLabel", label: "Status" },
  currentStatus: { key: "statusLabel", label: "Current Status" },
  bookingStatus: { key: "statusLabel", label: "Booking Status" },
  category: { key: "category", label: "Category" },
  paymentResponsibility: { key: "paymentResponsibility", label: "Payment Responsibility" },
  paymentType: { key: "paymentType", label: "Payment Type" },
  paymentStatus: { key: "paymentStatus", label: "Payment Status" },
  paymentMode: { key: "paymentMode", label: "Payment Mode" },
  transactionId: { key: "transactionId", label: "Transaction ID" },
  totalAmount: { key: "totalAmount", label: "Total Amount", type: "money" },
  paidAmount: { key: "paidAmount", label: "Paid Amount", type: "money" },
  discount: { key: "discount", label: "Discount", type: "money" },
};

const money = (key, label) => ({ key, label, type: "money" });

export const METRICS = {
  total: {
    title: "Total Bookings",
    file: "total-bookings",
    columns: [
      COL.bookingId, COL.guest, COL.contact, COL.hostel, COL.roomNo, COL.from, COL.to,
      COL.currentStatus, COL.paymentStatus,
      money("totalAmount", "Total"), money("paidAmount", "Paid"), money("balance", "Balance"),
    ],
    totalKeys: ["totalAmount", "paidAmount", "balance"],
  },
  received: {
    title: "Amount Received",
    file: "amount-received",
    columns: [
      COL.bookingId, COL.guest, COL.contact, COL.hostel, COL.roomNo, COL.bookingStatus,
      COL.paymentResponsibility, COL.paymentStatus, COL.paymentMode, COL.transactionId,
      COL.totalAmount, COL.paidAmount, COL.discount,
    ],
    totalKeys: ["totalAmount", "paidAmount", "discount"],
  },
  active: {
    title: "Active Guests",
    file: "active-guests",
    columns: [
      COL.bookingId, COL.guest, COL.contact, COL.hostel, COL.roomNo, COL.from, COL.expectedCheckout,
      COL.totalAmount, COL.paidAmount, money("pendingBalance", "Pending Balance"),
    ],
    totalKeys: ["totalAmount", "paidAmount", "pendingBalance"],
  },
  pending: {
    title: "Pending Amount",
    file: "pending-amount",
    columns: [
      COL.bookingId, COL.guest, COL.contact, COL.hostel, COL.roomNo, COL.from, COL.to,
      COL.category, COL.statusLabel, COL.paymentResponsibility, COL.paymentStatus,
      COL.totalAmount, COL.paidAmount, COL.discount, money("amount", "Pending Amount"),
    ],
    totalKeys: ["totalAmount", "paidAmount", "discount", "amount"],
  },
  cancelled: {
    title: "Cancelled Amount",
    file: "cancelled-amount",
    columns: [
      COL.bookingId, COL.guest, COL.contact, COL.hostel, COL.roomNo, COL.from, COL.to,
      COL.statusLabel, COL.totalAmount, COL.paidAmount, COL.discount, money("amount", "Cancelled Amount"),
    ],
    totalKeys: ["totalAmount", "paidAmount", "discount", "amount"],
  },
};

/** The one money column that matches the headline card (null for count-only metrics). */
export const HEADLINE_KEY = { received: "paidAmount", pending: "amount", cancelled: "amount" };

export const formatCell = (column, row) => {
  const value = row[column.key];
  if (column.type === "date") return fmtDate(value);
  return value === undefined || value === null || value === "" ? "—" : value;
};

export const filterRows = (metricKey, rows, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return rows;
  const { columns } = METRICS[metricKey];
  return rows.filter((row) =>
    columns.some((column) => String(formatCell(column, row)).toLowerCase().includes(q))
  );
};

/** Array-of-arrays for the sheet: header, one line per row, final Total row. */
export const buildExportSheet = (metricKey, rows) => {
  const { columns, totalKeys = [] } = METRICS[metricKey];
  const header = columns.map((column) => column.label);
  const body = rows.map((row) =>
    columns.map((column) => (column.type === "money" ? num(row[column.key]) : formatCell(column, row)))
  );

  const totalRow = columns.map((column, index) => {
    if (index === 0) return `TOTAL (${rows.length} rows)`;
    if (totalKeys.includes(column.key)) return sumBy(rows, column.key);
    return "";
  });

  return [header, ...body, totalRow];
};

export const getExportFileName = (metricKey, now = new Date()) =>
  `guest-room-${METRICS[metricKey].file}-${format(now, "yyyy-MM-dd")}.xlsx`;

/* ------------------------------------------------------------------ */
/* Trends (sorted by real period key, formatted only for display)      */
/* ------------------------------------------------------------------ */

const periodOf = (date, range, window) => {
  // Custom: daily buckets for spans up to ~a month, otherwise monthly
  if (range === "Custom") {
    const days = window?.start && window?.end ? differenceInCalendarDays(window.end, window.start) : 0;
    range = days <= 31 ? "Monthly" : "Annual";
  }
  switch (range) {
    case "Monthly": return { key: format(date, "yyyy-MM-dd"), label: format(date, "dd MMM") };
    case "Quarterly":
    case "Annual": return { key: format(date, "yyyy-MM"), label: format(date, "MMM yyyy") };
    default: return { key: String(getYear(date)), label: String(getYear(date)) };
  }
};

/** Groups bookings by check-in period. `valueOf(booking)` is summed (use () => 1 to count). */
export const buildTrend = (bookings, range, valueOf = () => 1, window = null) => {
  const buckets = new Map();

  bookings.forEach((booking) => {
    const from = toDate(booking.from);
    if (!from || Number.isNaN(from.getTime())) return;
    const value = valueOf(booking);
    if (!value) return;

    const { key, label } = periodOf(from, range, window);
    const bucket = buckets.get(key) || { key, period: label, value: 0 };
    bucket.value += value;
    buckets.set(key, bucket);
  });

  return Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
};

/* ------------------------------------------------------------------ */
/* Payment status distribution                                         */
/* ------------------------------------------------------------------ */

/**
 * Mutually-exclusive payment buckets that add up to the number of bookings:
 *  free          paymentType Free
 *  fullyPaid     Paid + PAID (any status)
 *  partiallyPaid Paid + booked/checked_in/checked_out + PARTIALLY_PAID
 *  unpaid        Paid + booked/checked_in/checked_out + UNPAID
 *  cancelledNoShow Paid + cancelled/no_show + not PAID
 */
export const getPaymentStatusCounts = (bookings) => {
  const paid = bookings.filter((b) => b.paymentType === "Paid");
  const receivable = paid.filter((b) => PENDING_STATUSES.includes(b.status));

  return {
    fullyPaid: paid.filter((b) => b.paymentStatus === "PAID").length,
    partiallyPaid: receivable.filter((b) => b.paymentStatus === "PARTIALLY_PAID").length,
    unpaid: receivable.filter((b) => b.paymentStatus === "UNPAID").length,
    cancelledNoShow: paid.filter(
      (b) => CANCELLED_STATUSES.includes(b.status) && b.paymentStatus !== "PAID"
    ).length,
    free: bookings.filter((b) => b.paymentType === "Free").length,
  };
};

/** Pure current booking.status distribution (raw status, no time-awareness). */
export const getBookingStatusCounts = (bookings) => {
  const count = (status) => bookings.filter((b) => b.status === status).length;
  return {
    booked: count("booked"),
    checkedIn: count("checked_in"),
    checkedOut: count("checked_out"),
    cancelled: count("cancelled"),
    noShow: count("no_show"),
  };
};
